import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, ShieldOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth, SSO_STATE_KEY } from '../lib/auth-context';

/**
 * Shown when a credential verifies (SSO or API key) but the API denies with
 * NOT_A_MEMBER / MEMBERSHIP_DISABLED — deny-by-default, no auto-provisioning.
 * Never a raw 401/403; always this calm, actionable explanation.
 *
 * Reused from two call sites:
 *  - `SsoCallback`, rendered inline while the browser still sits on
 *    `/auth/sso/callback?code=...&state=...` (pass `origin="sso"` so the copy can
 *    reference the Microsoft account by name).
 *  - `App`'s `Protected` guard, for an already-authenticated (password or API key)
 *    principal whose workspace membership check fails (no `origin` — generic copy).
 */
export function AccessDenied({
  adminEmail,
  reason,
  origin = 'access-check',
}: {
  adminEmail?: string;
  reason?: 'not_a_member' | 'disabled';
  origin?: 'sso' | 'access-check';
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    if (!adminEmail) return;
    await navigator.clipboard.writeText(adminEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /**
   * Clears the session (both the credential/principal store and any lingering SSO
   * state token) and navigates back to `/login` — must actually leave this page, since
   * `AccessDenied` can be rendered as a sub-view (from `SsoCallback`) while the browser
   * URL never changes, so clearing credentials alone is invisible to the user.
   */
  const backToLogin = () => {
    logout();
    window.sessionStorage.removeItem(SSO_STATE_KEY);
    navigate('/login', { replace: true });
  };

  const message =
    reason === 'disabled'
      ? "Your account was signed in successfully, but access to this workspace has been paused. Ask your workspace admin to re-enable it."
      : origin === 'sso'
        ? "Your Microsoft account is authenticated but hasn't been added to this workspace yet. Contact your workspace administrator to get access."
        : "You're signed in, but you don't have access to this workspace yet. Ask your workspace admin to add you.";

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken p-6">
      <Card className="max-w-md">
        <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
          <div className="rounded-full bg-brand-subtle p-3 text-brand-hover">
            <ShieldOff size={28} />
          </div>
          <h1 className="text-lg font-semibold text-ink">Almost there</h1>
          <p className="text-sm text-ink-muted">{message}</p>
          {adminEmail ? (
            <div className="flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-surface-sunken px-3 py-2">
              <span className="truncate text-sm text-ink">{adminEmail}</span>
              <Button variant="ghost" size="sm" onClick={copyEmail} aria-label="Copy admin email">
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>
          ) : null}
          <Button variant="outline" onClick={backToLogin} className="mt-2 w-full">
            Sign in with a different account
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
